import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { CountdownTimer } from "../../../components/inputs/countdownTimer";
import { TextInput } from "../../../components/inputs/textInput";
import { InfoComponent } from "../../../components/notification/infoComponent";
import { AuthHeader } from "../../../components/typography/authHeader";
import {
  useForgotPasswordMutation,
  useResendOtpMutation,
  useVerifyEmailMutation,
} from "../../../redux/features/auth/auth.api";
import { BaseApiErrorResponse } from "../../../utils/types";
import {
  ForgotPasswordSchema,
  VerifySignUpSchema,
} from "../../../utils/validations";

const ConfirmResetCode = ({ email }: { email: string }) => {
  const [canResendCode, setCanResendCode] = useState(false);

  const [verifyEmail, { isLoading, isError, error }] = useVerifyEmailMutation();
  const [
    resendOtp,
    { isLoading: resendingOtp, isError: isResendError, error: resendError },
  ] = useResendOtpMutation();

  const navigate = useNavigate();

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    setFieldError,
  } = useFormik({
    initialValues: {
      code: "",
    },
    validationSchema: VerifySignUpSchema,
    onSubmit: async (vals) => {
      const response = await verifyEmail({
        userOtp: vals.code,
      });

      if (response.data?.status === "success") {
        navigate(`/reset-password?email=${encodeURIComponent(email)}`);
      }
    },
  });

  useEffect(() => {
    if (isError) {
      setFieldError(
        "code",
        (error as unknown as BaseApiErrorResponse)?.data?.message ??
          "An error occurred!",
      );
    }
    // eslint-disable-next-line
  }, [isError, error]);
  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <AuthHeader
        header="Verify your email"
        text={`Enter the code we sent to ${email} to verify your account`}
      />

      <div className="mt-10 flex flex-col gap-5">
        {isResendError && (
          <InfoComponent
            title="Error"
            text={
              (resendError as unknown as BaseApiErrorResponse)?.data?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}

        <TextInput
          type="text"
          name="code"
          value={values.code}
          placeholder="Enter Verification Code"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Verification Code"
          errors={errors.code && touched.code ? errors.code : ""}
          autoFocus
        />

        <div className="mt-5 flex w-full flex-col gap-4 text-center">
          {!canResendCode && (
            <CountdownTimer
              onClick={() => {}}
              text="Resend code"
              onTimerComplete={() => {
                setCanResendCode(true);
              }}
            />
          )}

          <Button
            type="submit"
            text="Continue"
            variation="primary"
            className=""
            loading={isLoading}
            disabled={isLoading}
          />
        </div>

        {canResendCode && (
          <Button
            type="button"
            text="Resend Code"
            variation="plain"
            className=""
            loading={resendingOtp}
            disabled={resendingOtp}
            onClick={async () => {
              const response = await resendOtp({ email: email });
              if (response.data?.status === "success") {
                setCanResendCode(false);
              }
            }}
          />
        )}
      </div>
    </form>
  );
};

export const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const [forgotPassword, { isLoading, isError, error, isSuccess }] =
    useForgotPasswordMutation();

  const { values, handleBlur, handleChange, handleSubmit, errors, touched } =
    useFormik({
      initialValues: {
        email: searchParams.get("email") ?? "",
      },
      enableReinitialize: true,
      validationSchema: ForgotPasswordSchema,
      onSubmit: async (vals) => {
        await forgotPassword({
          email: vals.email,
        });
      },
    });
  return isSuccess ? (
    <ConfirmResetCode email={values.email} />
  ) : (
    <form className="w-full" onSubmit={handleSubmit}>
      <AuthHeader
        header="Forgot Password?"
        text={"Enter the email address associated with your account."}
      />

      <div className="mt-10 flex flex-col gap-5">
        {isError && (
          <InfoComponent
            title="Error"
            text={
              (error as unknown as BaseApiErrorResponse)?.data?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}

        <TextInput
          type="text"
          name="email"
          value={values.email}
          placeholder="e.g.example@email.com"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Email"
          errors={errors.email && touched.email ? errors.email : ""}
          autoFocus
        />

        <Button
          type="submit"
          text="Reset Password"
          variation="primary"
          className="mt-5"
          loading={isLoading}
          disabled={isLoading}
        />

        <Button
          type="button"
          text="Back to Log In"
          variation="plain"
          onClick={() => {
            navigate("/login");
          }}
        />
      </div>
    </form>
  );
};
